import React, {useEffect, useRef, useState} from 'react'
import classnames from 'classnames'
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from '@mui/material/CircularProgress';
import {useNavigate} from "react-router-dom";

import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import {FILTER_URL_KEYS, isDefaultFilterValue, useListDealsStore} from "../../stores/listDealsStore";
import {useShallow} from "zustand/react/shallow";
import {useUserContext} from "../../contexts/UserContext";

// Build the Users dropdown options from the account's users, always led by
// "All". account_users entries may be plain strings or user objects — derive a
// readable label either way.
const buildUserOptions = (accountUsers) => {
    const labels = (accountUsers ?? []).map((u) => {
        if (typeof u === 'string') return u
        if (!u) return ''
        return u.name
            || u.full_name
            || [u.first_name, u.last_name].filter(Boolean).join(' ')
            || u.email
            || String(u.id ?? '')
    }).filter(Boolean)
    return ['All', ...labels]
}

// ─────────────────────────────────────────────────────────────────────────
// Static option lists. The cascading lists (Make / Model / Trim) live in
// the store and are fetched from the backend — see fetchMakes /
// fetchModelsForMake / fetchTrimsForModel in listDealsStore.
// ─────────────────────────────────────────────────────────────────────────
const STATUS_OPTIONS    = ['All', 'Active', 'Expired']
const DEAL_TYPE_OPTIONS = ['All', 'Lease', 'Finance', 'Cash']

// Year filter options — the year before, current year, and the year
// after. Listed newest-first so the dropdown reads top-down by recency.
const yearOptions = () => {
    const y = new Date().getFullYear()
    return ['All', String(y + 1), String(y), String(y - 1)]
}

// ── Custom dropdown ────────────────────────────────────────────────────
// Renders as "Label: value ▾". Click toggles the menu. Click outside
// closes it. Only one is open at a time — caller passes isOpen/onToggle.
//   disabled — greys out, ignores clicks (parent filter empty).
//   loading  — shows a spinner where the chevron normally sits.
const FilterDropdown = ({
    label, value, options, onChange, isOpen, onToggle,
    disabled = false, loading = false,
}) => {
    const ref = useRef(null)

    useEffect(() => {
        if (!isOpen) return
        const onDocMouseDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                onToggle(false)
            }
        }
        document.addEventListener('mousedown', onDocMouseDown)
        return () => document.removeEventListener('mousedown', onDocMouseDown)
    }, [isOpen, onToggle])

    return (
        <div className={'filter-dropdown'} ref={ref}>
            <button
                type={'button'}
                className={classnames('filter-dropdown-btn', {open: isOpen, disabled})}
                disabled={disabled}
                onClick={() => { if (!disabled) onToggle(!isOpen) }}
            >
                <span className={'filter-dropdown-label'}>
                    {label}: <span className={'filter-dropdown-value'}>{value}</span>
                </span>
                {loading ? (
                    <CircularProgress className={'spinner'} size={11} thickness={5}/>
                ) : (
                    <svg className={'chevron'} width="10" height="6" viewBox="0 0 10 6" fill="none">
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5"
                              strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                )}
            </button>
            {isOpen && (
                <div className={'filter-dropdown-menu'}>
                    {options.map((opt) => (
                        <div
                            key={opt}
                            className={classnames('filter-dropdown-item', {selected: opt === value})}
                            onClick={(e) => {
                                e.stopPropagation()
                                onChange(opt)
                                onToggle(false)
                            }}
                        >
                            {opt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// ── Multi-select dropdown ──────────────────────────────────────────────
// Like FilterDropdown but lets the user pick several options at once. The value
// is a comma-joined string of picks ("Alice,Bob"); "All" (or empty) means no
// filter. Toggling an item keeps the menu open; the "All" row clears selection.
const MultiFilterDropdown = ({
    label, value, options, onChange, isOpen, onToggle, disabled = false,
}) => {
    const ref = useRef(null)

    useEffect(() => {
        if (!isOpen) return
        const onDocMouseDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onToggle(false)
        }
        document.addEventListener('mousedown', onDocMouseDown)
        return () => document.removeEventListener('mousedown', onDocMouseDown)
    }, [isOpen, onToggle])

    const selected = (!value || value === 'All') ? [] : String(value).split(',')
    const items = options.filter((o) => o !== 'All')
    const display = selected.length === 0 ? 'All'
        : selected.length === 1 ? selected[0]
        : `${selected.length} selected`

    const toggle = (opt) => {
        const next = selected.includes(opt)
            ? selected.filter((s) => s !== opt)
            : [...selected, opt]
        onChange(next.length ? next.join(',') : 'All')
    }

    return (
        <div className={'filter-dropdown'} ref={ref}>
            <button
                type={'button'}
                className={classnames('filter-dropdown-btn', {open: isOpen, disabled})}
                disabled={disabled}
                onClick={() => { if (!disabled) onToggle(!isOpen) }}
            >
                <span className={'filter-dropdown-label'}>
                    {label}: <span className={'filter-dropdown-value'}>{display}</span>
                </span>
                <svg className={'chevron'} width="10" height="6" viewBox="0 0 10 6" fill="none">
                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5"
                          strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            {isOpen && (
                <div className={'filter-dropdown-menu'}>
                    <div
                        className={classnames('filter-dropdown-item multi', {selected: selected.length === 0})}
                        onClick={(e) => { e.stopPropagation(); onChange('All') }}
                    >
                        <span className={'multi-check'}/>
                        All
                    </div>
                    {items.map((opt) => (
                        <div
                            key={opt}
                            className={classnames('filter-dropdown-item multi', {selected: selected.includes(opt)})}
                            onClick={(e) => { e.stopPropagation(); toggle(opt) }}
                        >
                            <span className={'multi-check'}/>
                            {opt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// ── Numeric min/max pill ───────────────────────────────────────────────
const PriceRange = ({label, min, max, onMinChange, onMaxChange}) => (
    <div className={'price-range'}>
        <span className={'price-range-label'}>{label}</span>
        <span className={'dollar'}>$</span>
        <input
            type={'number'}
            placeholder={'min'}
            value={min}
            onChange={(e) => onMinChange(e.target.value)}
        />
        <span className={'dash'}>–</span>
        <span className={'dollar'}>$</span>
        <input
            type={'number'}
            placeholder={'max'}
            value={max}
            onChange={(e) => onMaxChange(e.target.value)}
        />
    </div>
)


export const ListDealsPageHeader = () => {
    const isTabletOrMobile = useMediaQuery({query: mediaQueryForMobile})

    // All filter values + their setter live in the store now. The store is
    // kept in sync with the URL by `useFilterUrlSync` (called by the page).
    const navigate = useNavigate()

    const { profileState } = useUserContext()
    const userOptions = buildUserOptions(profileState?.account_users)

    const {
        search,
        isLoading,
        setSearch,
        setFilter: storeSetFilter,
        viewMode, setViewMode,
        users, status, dealType, year, make, model, trim,
        monthlyMin, monthlyMax, msrpMin, msrpMax,

        // Cascading option lists + loading flags (Make / Model / Trim).
        makeOptions, modelOptions, trimOptions,
        isLoadingMakes, isLoadingModels, isLoadingTrims,
        fetchMakes, fetchModelsForMake, fetchTrimsForModel,

        clearFilters,
    } = useListDealsStore(
        useShallow((state) => ({
            search:             state.search,
            isLoading:          state.isLoading,
            setSearch:          state.setSearch,
            setFilter:          state.setFilter,
            viewMode:           state.viewMode,
            setViewMode:        state.setViewMode,
            users:              state.users,
            status:             state.status,
            dealType:           state.dealType,
            year:               state.year,
            make:               state.make,
            model:              state.model,
            trim:               state.trim,
            monthlyMin:         state.monthlyMin,
            monthlyMax:         state.monthlyMax,
            msrpMin:            state.msrpMin,
            msrpMax:            state.msrpMax,
            makeOptions:        state.makeOptions,
            modelOptions:       state.modelOptions,
            trimOptions:        state.trimOptions,
            isLoadingMakes:     state.isLoadingMakes,
            isLoadingModels:    state.isLoadingModels,
            isLoadingTrims:     state.isLoadingTrims,
            fetchMakes:         state.fetchMakes,
            fetchModelsForMake: state.fetchModelsForMake,
            fetchTrimsForModel: state.fetchTrimsForModel,
            clearFilters:       state.clearFilters,
        }))
    )

    // Active when any user-facing filter is non-default. Drives both the
    // disabled state of the "Clear all" button and its emphasis.
    const liveFilters = {
        search, status, dealType, year, make, model, trim, users,
        monthlyMin, monthlyMax, msrpMin, msrpMax,
    }
    const hasActiveFilters = Object.keys(FILTER_URL_KEYS).some(
        (key) => !isDefaultFilterValue(key, liveFilters[key])
    )

    // Pure UI state — never goes to the URL or the store.
    const [filtersOpen, setFiltersOpen] = useState(false)
    const [openDropdown, setOpenDropdown] = useState(null)

    const toggleDropdown = (id) => (open) => setOpenDropdown(open ? id : null)
    const setFilter = (key) => (value) => storeSetFilter(key, value)

    // ── Cascading option fetches ────────────────────────────────────────
    // Refetch the make list every time the year changes (and on mount).
    // The /makesForYear endpoint currently ignores the year path-param,
    // but the cascade convention mirrors CalcSelectCar so the wiring is
    // future-proof if the backend starts respecting it.
    useEffect(() => { fetchMakes() }, [year])

    // Whenever `make` or `year` changes, refetch model options. Also reset
    // model + trim to 'All' when the make actually changed (vs. initial
    // mount or a year-only change) so stale dependent selections don't
    // produce empty result sets.
    const prevMakeRef = useRef(make)
    useEffect(() => {
        if (prevMakeRef.current !== make) {
            if (model !== 'All') storeSetFilter('model', 'All')
            if (trim  !== 'All') storeSetFilter('trim', 'All')
            prevMakeRef.current = make
        }
        fetchModelsForMake(make, year)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [make, year])

    // Same pattern for trims, scoped to model changes.
    const prevModelRef = useRef(model)
    useEffect(() => {
        if (prevModelRef.current !== model) {
            if (trim !== 'All') storeSetFilter('trim', 'All')
            prevModelRef.current = model
        }
        fetchTrimsForModel(make, model, year)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [make, model, year])

    return (
        <React.Fragment>
            {/* ── Title row ─────────────────────────────────────────── */}
            <div
                className={'ListDealsPage_header'}
                style={{flexDirection: isTabletOrMobile ? 'column' : 'row'}}
            >
                <div className={'ListDealsPage_header-name'}>
                    <Typography
                        sx={{fontSize: '24px', color: 'var(--text-primary)'}}
                        component="div"
                    >
                        Quotes
                    </Typography>
                </div>

                <div className={'ListDealsPage_header-actions'}>
                    <div className={'view-toggle'}>
                        <button
                            type={'button'}
                            className={classnames('view-btn', {active: viewMode === 'grid'})}
                            onClick={() => setViewMode('grid')}
                            aria-label={'Grid view'}
                        >
                            ⊞
                        </button>
                        <button
                            type={'button'}
                            className={classnames('view-btn', {active: viewMode === 'list'})}
                            onClick={() => setViewMode('list')}
                            aria-label={'List view'}
                        >
                            ☰
                        </button>
                    </div>
                    <button
                        type={'button'}
                        className={'btn-new-quote'}
                        onClick={() => navigate('/calculate')}
                    >
                        + New Quote
                    </button>
                </div>
            </div>

            {/* ── Filter bar: Users  |  centered Filters toggle  |  Search ── */}
            <div className={'ListDealsPage_filter-bar'}>
                <MultiFilterDropdown
                    label={'Users'} value={users} options={userOptions}
                    onChange={setFilter('users')}
                    isOpen={openDropdown === 'users'}
                    onToggle={toggleDropdown('users')}
                />
                {hasActiveFilters && (
                    <button
                        type={'button'}
                        className={'filter-clear-btn'}
                        onClick={clearFilters}
                    >
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5"
                                  strokeLinecap="round"/>
                        </svg>
                        <span>Clear all filters</span>
                    </button>
                )}
                <button
                    type={'button'}
                    className={classnames('filter-toggle', {open: filtersOpen})}
                    onClick={() => setFiltersOpen((v) => !v)}
                >
                    <span>Filters</span>
                    <svg className={'filter-chevron'} viewBox="0 0 24 16" fill="none"
                         stroke="currentColor" strokeWidth="2.5"
                         strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2 2 12 13 22 2"/>
                    </svg>
                </button>

                <div className={'filter-search-wrap'}>
                    <SearchIcon className={'search-icon'}/>
                    <input
                        type="text"
                        value={search}
                        placeholder={'Search make, model, trim...'}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {isLoading && (
                        <CircularProgress size={16} className={'search-spinner'}/>
                    )}
                </div>
            </div>

            {/* ── Collapsible filter shelf ──────────────────────────── */}
            <div className={classnames('ListDealsPage_filter-shelf', {open: filtersOpen})}>
                <div className={'filter-shelf-inner'}>
                    {/* Row 1 — Status / Deal Type */}
                    <div className={'filter-row'}>
                        <FilterDropdown
                            label={'Status'} value={status} options={STATUS_OPTIONS}
                            onChange={setFilter('status')}
                            isOpen={openDropdown === 'status'}
                            onToggle={toggleDropdown('status')}
                        />
                        <FilterDropdown
                            label={'Deal Type'} value={dealType} options={DEAL_TYPE_OPTIONS}
                            onChange={setFilter('dealType')}
                            isOpen={openDropdown === 'dealType'}
                            onToggle={toggleDropdown('dealType')}
                        />
                    </div>

                    {/* Row 2 — Year / Make / Model / Trim */}
                    <div className={'filter-row'}>
                        <FilterDropdown
                            label={'Year'} value={year} options={yearOptions()}
                            onChange={setFilter('year')}
                            isOpen={openDropdown === 'year'}
                            onToggle={toggleDropdown('year')}
                        />
                        <FilterDropdown
                            label={'Make'} value={make} options={makeOptions}
                            onChange={setFilter('make')}
                            isOpen={openDropdown === 'make'}
                            onToggle={toggleDropdown('make')}
                            loading={isLoadingMakes}
                        />
                        <FilterDropdown
                            label={'Model'} value={model} options={modelOptions}
                            onChange={setFilter('model')}
                            isOpen={openDropdown === 'model'}
                            onToggle={toggleDropdown('model')}
                            loading={isLoadingModels}
                            disabled={!make || make === 'All'}
                        />
                        <FilterDropdown
                            label={'Trim'} value={trim} options={trimOptions}
                            onChange={setFilter('trim')}
                            isOpen={openDropdown === 'trim'}
                            onToggle={toggleDropdown('trim')}
                            loading={isLoadingTrims}
                            disabled={!model || model === 'All'}
                        />
                    </div>

                    {/* Row 3 — Monthly / MSRP ranges */}
                    <div className={'filter-row'}>
                        <PriceRange
                            label={'MONTHLY'}
                            min={monthlyMin} max={monthlyMax}
                            onMinChange={setFilter('monthlyMin')}
                            onMaxChange={setFilter('monthlyMax')}
                        />
                        <PriceRange
                            label={'MSRP'}
                            min={msrpMin} max={msrpMax}
                            onMinChange={setFilter('msrpMin')}
                            onMaxChange={setFilter('msrpMax')}
                        />
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}
